import React from 'react';
import ArticlePage from './ArticlePage';

function UncertaintyPage() {
  const articleContent = (
    <>
      <h2>The Uncertainty Problem in Strategic Modeling</h2>
      <p>
        Every model is wrong. The question is whether it's useful. For strategic decision support, 
        usefulness depends critically on how well uncertainty is incorporated and communicated. 
        Point forecasts create false confidence; probability distributions overwhelm decision-makers. 
        The challenge is finding the right level of uncertainty representation for the decision at hand.
      </p>
      <p>
        This article develops a practical framework for quantifying, propagating, and communicating 
        uncertainty in simulation models—one that maintains rigor while remaining accessible to 
        non-technical executives.
      </p>

      <h2>Sources of Uncertainty</h2>
      <p>
        Strategic models face three distinct types of uncertainty:
      </p>
      
      <h3>Parametric Uncertainty</h3>
      <p>
        Model parameters (growth rates, conversion rates, capacity limits) are estimated from historical 
        data and expert judgment. Each carries confidence intervals that should propagate through 
        the model to outputs.
      </p>

      <h3>Structural Uncertainty</h3>
      <p>
        The model structure itself is a hypothesis about how the system works. Alternative structures 
        may be equally plausible. Ensemble approaches can capture this by running multiple model 
        specifications.
      </p>

      <h3>Scenario Uncertainty</h3>
      <p>
        External factors (market conditions, competitor actions, regulatory changes) follow unknowable 
        paths. Scenario analysis explores discrete alternative futures; Monte Carlo methods sample 
        from probability distributions.
      </p>

      <h2>The Communication Challenge</h2>
      <p>
        Technical accuracy in uncertainty quantification is necessary but not sufficient. Decision-makers 
        need uncertainty communicated in ways that:
      </p>
      <ul>
        <li><strong>Support intuition:</strong> Fan charts and confidence intervals should map to how executives already think about risk</li>
        <li><strong>Enable comparison:</strong> Uncertainty across scenarios must be comparable, not just within scenarios</li>
        <li><strong>Highlight decisions:</strong> The focus should be on where uncertainty matters for the choice at hand</li>
        <li><strong>Invite calibration:</strong> Stakeholders should be able to test "what if we're wrong about X?"</li>
      </ul>

      <div className="my-8">
        <img 
          src="/images/uncertainty-square.png" 
          alt="A Practical Framework for Uncertainty" 
          className="w-full h-auto rounded-lg"
          loading="lazy"
        />
      </div>

      <h2>A Practical Framework</h2>
      <p>
        Our framework proposes a four-level approach to uncertainty in strategic models:
      </p>
      <ol>
        <li><strong>Level 1 - Point estimates:</strong> Single best-guess projections for screening and communication</li>
        <li><strong>Level 2 - Sensitivity ranges:</strong> High/low bounds on key parameters for quick risk assessment</li>
        <li><strong>Level 3 - Scenario ensembles:</strong> Discrete alternative futures for strategic planning</li>
        <li><strong>Level 4 - Probabilistic distributions:</strong> Full Monte Carlo for investment decisions and risk quantification</li>
      </ol>
      <p>
        The appropriate level depends on decision stakes, available data, and organizational capacity 
        for probabilistic reasoning.
      </p>

      <h2>Implications for Practice</h2>
      <p>
        Organizations that explicitly address uncertainty in their strategic models report higher 
        confidence in decisions, fewer surprises during execution, and better post-decision learning. 
        The investment in uncertainty quantification pays off not just in better decisions, but in 
        organizational learning and adaptive capacity.
      </p>
    </>
  );

  const relatedArticles = [
    { category: 'Article', title: 'System Dynamics in Enterprise Decision Making', href: '/innovation/system-dynamics' },
    { category: 'Article', title: 'AI-Enhanced Simulation Modeling', href: '/innovation/ai-simulation' },
  ];

  return (
    <ArticlePage
      category="Article"
      title="Quantifying Uncertainty in Strategic Models"
      subtitle="Methods for incorporating and communicating uncertainty in business simulations—a practical framework for probabilistic scenario analysis."
      heroImage="/images/uncertainty-hero.png"
      articleContent={articleContent}
      relatedArticles={relatedArticles}
    />
  );
}

export default UncertaintyPage;
